import { getDealRevenueCents } from '../../../../shared/utils/dealRevenue';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  applyClientSummaryToDealForm,
  buildDealPayload,
  clearClientSummaryFromDealForm,
  getInitialDealFormState,
  getDealKindMeta,
  commitInstallmentEdit,
  formatCentsForInput,
  isValidMoneyDraft,
  splitInstallments,
  validateDealForm,
} from '@/modules/deals/shared/utils/dealAdapters';
import { toast } from '../../../../shared/hooks/use-toast';

export function useDealForm({
  dealType,
  stages,
  clients,
  onClose,
  onCreate,
  onRequestNewClient,
  isOpen = false,
}) {
  const [selectedClientId, setSelectedClientId] = useState('');
  const [formState, setFormState] = useState(() => getInitialDealFormState(dealType, stages));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [installmentDrafts, setInstallmentDrafts] = useState({});
  const submittingRef = useRef(false);
  const activeSubmissionRef = useRef(null);
  const clientSubmissionIdRef = useRef(null);
  const wasOpenRef = useRef(false);
  const meta = useMemo(() => getDealKindMeta(dealType), [dealType]);

  function createClientSubmissionId() {
    if (typeof globalThis.crypto?.randomUUID === 'function') {
      return globalThis.crypto.randomUUID();
    }

    return `webtika-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  function reset({ clearSubmission = false } = {}) {
    setFormState(getInitialDealFormState(dealType, stages));
    setSelectedClientId('');
    setInstallmentDrafts({});
    if (clearSubmission) {
      clientSubmissionIdRef.current = null;
    }
  }

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      clientSubmissionIdRef.current = dealType === 'proposta' ? createClientSubmissionId() : null;
    }
    wasOpenRef.current = isOpen;
  }, [dealType, isOpen]);

  function updateField(field, value) {
    if (field === meta.amountField || field === 'probability') setInstallmentDrafts({});
    setFormState((prev) => {
      if (field !== meta.amountField && field !== 'probability') return { ...prev, [field]: value };
      const totalCents = getDealRevenueCents({ ...prev, [field]: value });
      const count = prev.dilazionamento.scadenze.length || 1;
      return {
        ...prev,
        [field]: value,
        dilazionamento: {
          ...prev.dilazionamento,
          importiRate: splitInstallments(totalCents, count),
          amountError: '',
          rate: count,
        },
      };
    });
  }

  function updateNestedField(group, field, value) {
    setFormState((prev) => ({
      ...prev,
      [group]: {
        ...prev[group],
        [field]: value,
      },
    }));
  }

  function handleClientSelect(clientId) {
    setSelectedClientId(clientId);

    if (clientId === 'nuovo') {
      setFormState((prev) => clearClientSummaryFromDealForm(prev));
      onRequestNewClient?.();
      return;
    }

    const client = clients.find((item) => item.id.toString() === clientId);
    if (!client) {
      setSelectedClientId('');
      setFormState((prev) => clearClientSummaryFromDealForm(prev));
      return;
    }

    setFormState((prev) => applyClientSummaryToDealForm(prev, client));
  }

  function addScadenza() {
    setInstallmentDrafts({});
    setFormState((prev) => ({
      ...prev,
      dilazionamento: {
        ...prev.dilazionamento,
        scadenze: [...prev.dilazionamento.scadenze, ''],
        amountError: '',
        rate: prev.dilazionamento.scadenze.length + 1,
        importiRate: splitInstallments(
          getDealRevenueCents(prev),
          prev.dilazionamento.scadenze.length + 1
        ),
      },
    }));
  }

  function updateScadenza(index, value) {
    setFormState((prev) => {
      const scadenze = [...prev.dilazionamento.scadenze];
      scadenze[index] = value;

      return {
        ...prev,
        dilazionamento: {
          ...prev.dilazionamento,
          scadenze,
          rate: scadenze.length,
        },
      };
    });
  }

  function removeScadenza(index) {
    setInstallmentDrafts({});
    setFormState((prev) => {
      const scadenze = prev.dilazionamento.scadenze.filter((_, itemIndex) => itemIndex !== index);

      return {
        ...prev,
        dilazionamento: {
          ...prev.dilazionamento,
          scadenze: scadenze.length > 0 ? scadenze : [''],
          amountError: '',
          rate: Math.max(1, scadenze.length),
          importiRate: splitInstallments(
            getDealRevenueCents(prev),
            Math.max(1, scadenze.length)
          ),
        },
      };
    });
  }

  function beginInstallmentAmountEdit(index) {
    setInstallmentDrafts((prev) => ({
      ...prev,
      [index]: prev[index] ?? formatCentsForInput(formState.dilazionamento.importiRate?.[index]),
    }));
  }

  function updateInstallmentAmount(index, value) {
    if (!isValidMoneyDraft(value)) return;
    setInstallmentDrafts((prev) => ({ ...prev, [index]: value }));
  }

  function commitInstallmentAmount(index) {
    if (!Object.prototype.hasOwnProperty.call(installmentDrafts, index)) return;
    setFormState((prev) => {
      const result = commitInstallmentEdit(
        prev.dilazionamento.importiRate,
        index,
        installmentDrafts[index] ?? '',
        getDealRevenueCents(prev)
      );
      if (!result.error) {
        setInstallmentDrafts({});
      }
      return {
        ...prev,
        dilazionamento: {
          ...prev.dilazionamento,
          importiRate: result.amounts.length ? result.amounts : prev.dilazionamento.importiRate,
          amountError: result.error,
        },
      };
    });
  }

  function addTeamMember() {
    setFormState((prev) => ({
      ...prev,
      teamCoinvolto: [...prev.teamCoinvolto, ''],
    }));
  }

  function updateTeamMember(index, value) {
    setFormState((prev) => {
      const teamCoinvolto = [...prev.teamCoinvolto];
      teamCoinvolto[index] = value;

      return {
        ...prev,
        teamCoinvolto,
      };
    });
  }

  function removeTeamMember(index) {
    setFormState((prev) => ({
      ...prev,
      teamCoinvolto: prev.teamCoinvolto.filter((_, itemIndex) => itemIndex !== index),
    }));
  }

  function handleSubmit() {
    if (submittingRef.current) {
      return activeSubmissionRef.current;
    }

    const validationError = validateDealForm(formState);
    if (validationError) {
      toast({ title: 'Affare non valido', description: validationError, variant: 'destructive' });
      return Promise.resolve({
        ok: false,
        error: 'DEAL_VALIDATION_ERROR',
        message: validationError,
      });
    }

    if (dealType === 'proposta' && !clientSubmissionIdRef.current) {
      clientSubmissionIdRef.current = createClientSubmissionId();
    }
    const payload = buildDealPayload(
      {
        ...formState,
        ...(dealType === 'proposta'
          ? { clientSubmissionId: clientSubmissionIdRef.current }
          : {}),
      },
      dealType
    );

    submittingRef.current = true;
    setIsSubmitting(true);
    const submission = (async () => {
      try {
        const result = await onCreate(payload);
        if (result?.ok === false) {
          return result;
        }

        reset({ clearSubmission: true });
        onClose();
        return result;
      } finally {
        submittingRef.current = false;
        activeSubmissionRef.current = null;
        setIsSubmitting(false);
      }
    })();
    activeSubmissionRef.current = submission;
    return submission;
  }

  function handleClose() {
    if (submittingRef.current) return;
    reset({ clearSubmission: true });
    onClose();
  }

  return {
    meta,
    formState,
    selectedClientId,
    isSubmitting,
    updateField,
    updateNestedField,
    handleClientSelect,
    addScadenza,
    updateScadenza,
    updateInstallmentAmount,
    beginInstallmentAmountEdit,
    commitInstallmentAmount,
    installmentDrafts,
    removeScadenza,
    addTeamMember,
    updateTeamMember,
    removeTeamMember,
    handleSubmit,
    handleClose,
  };
}
